import {
  AfterViewInit,
  DestroyRef,
  Directive,
  inject,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Route,
  Router,
  type Routes,
} from '@angular/router';
import { filter, merge } from 'rxjs';
import { asMenu, MenuItem } from './menu-item';
import { MenuRefreshService } from './menu-refresh.service';

export abstract class MenuComponent {
  public abstract readonly menu: WritableSignal<MenuItem[]>;
}

@Directive({
  standalone: true,
})
export class MenuDirective implements AfterViewInit {
  private readonly component = inject(MenuComponent, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly menuRefresh = inject(MenuRefreshService);
  private autoGeneratedMenu = false;
  private lastAutoGeneratedMenu?: MenuItem[];

  public ngAfterViewInit(): void {
    this.updateMenu();

    merge(
      this.router.events.pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      ),
      this.menuRefresh.refresh$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateMenu());
  }

  private updateMenu(): void {
    const component = this.component;

    if (component == null) {
      return;
    }

    const currentMenu = component.menu();

    if (
      this.autoGeneratedMenu &&
      currentMenu != null &&
      currentMenu !== this.lastAutoGeneratedMenu
    ) {
      this.autoGeneratedMenu = false;
      return;
    }

    if (this.autoGeneratedMenu === false && currentMenu?.length !== 0) {
      return;
    }

    const menu = asMenu({
      routeConfig: getCurrentRouteConfig(this.route, this.router.config),
    });

    component.menu.set(menu);
    this.lastAutoGeneratedMenu = menu;
    this.autoGeneratedMenu = true;
  }
}

function getCurrentRouteConfig(
  route: ActivatedRoute,
  routes: Routes,
): Route | undefined {
  const routeChain = route.snapshot.pathFromRoot
    .map(snapshot => snapshot.routeConfig)
    .filter((config): config is Route => config != null);

  if (routeChain.length === 0) {
    return route.routeConfig ?? undefined;
  }

  let currentRoutes = routes;
  let matchedRoute: Route | undefined;

  for (const routeConfig of routeChain) {
    matchedRoute = findMatchingRoute(routeConfig, currentRoutes);

    if (matchedRoute == null) {
      return route.routeConfig ?? undefined;
    }

    currentRoutes = matchedRoute.children ?? [];
  }

  return matchedRoute;
}

function findMatchingRoute(
  targetRoute: Route,
  routes: readonly Route[],
): Route | undefined {
  return routes.find(
    route =>
      route.path === targetRoute.path &&
      route.outlet === targetRoute.outlet &&
      route.redirectTo === targetRoute.redirectTo &&
      route.component === targetRoute.component &&
      route.loadComponent === targetRoute.loadComponent &&
      route.loadChildren === targetRoute.loadChildren,
  );
}
